<template>
	<div>
		<div v-for="field in fields" v-bind:key="field.Name">
			<b-form-group v-if="field.Visible"
				:label="field.Label"
				:label-for="field.Name"
				:label-cols-sm="field.IsLabelDisplayed ? 4 : 0"
				:label-sr-only="!(field.IsLabelDisplayed)"
				label-align-sm="left"
				class="mb-1">

				<b-input-group v-if="(field.Type === fieldTypeEnum.Text || field.Type === fieldTypeEnum.Number || field.Type === fieldTypeEnum.Date)"
					:prepend="field.PreText" :append="field.PostText">

					<b-form-input v-if="field.Type === fieldTypeEnum.Text"
						type="text"
						:id="field.Name"
						:disabled="ConsultMode === objectDisplayingModeEnum.Read"
						:readonly="field.ReadOnly"
						:required="field.Required"
						:state="field.State"
						v-model="field.Value"
						@input="updateValue(field)" />

					<b-form-input v-if="field.Type === fieldTypeEnum.Number"
						type="number"
						:id="field.Name"
						:disabled="ConsultMode === objectDisplayingModeEnum.Read || field.Disabled"
						:readonly="field.ReadOnly"
						:required="field.Required"
						:state="field.State"
						:min="field.MinValue"
						:max="field.MaxValue"
						v-model="field.Value"
						@keypress="isNumber($event, field)"
						@input="updateValue(field)"
						:step="field.Step ? field.Step : 0" />

					<DatePicker v-if="field.Type === fieldTypeEnum.Date" v-model="field.Value" :Variant="Variant" :Locale="$store.getters['SessionStore/GetLanguage']" :InputId="field.Name" :Disabled="ConsultMode === objectDisplayingModeEnum.Read" :ReadOnly="field.ReadOnly" :Required="field.Required" :State.sync="field.State" />
					
					<template v-if="field.PostText || field.Tooltip" #append>
						<div v-if="field.PostText" class="input-group-text">{{field.PostText}}</div>
						<b-badge v-if="field.Tooltip" v-b-tooltip.noninteractive.hover :title="field.Tooltip" circle class="align-self-center ml-2"><b-icon font-scale="1.5" icon="info" /></b-badge>
					</template>

					<!-- Bouton de test d'invariant en doublons -->
					<template v-if="field.IsDuplicateCheckerDisplayed" #prepend >
						<b-button size="sm" v-b-tooltip.noninteractive.hover :title="$t('CheckDuplicates')" @click="checkDuplicate(field.Value)" :disabled="!(field.Value)" >
							<b-icon icon="lightning-fill"  />
						</b-button>
					</template>

				</b-input-group>

				<!-- Notes -->
				<b-input-group v-if="field.Type === fieldTypeEnum.Mark"
					:prepend="field.PreText" :append="field.PostText">
					<b-form-input
						:type="field.Value.AnswerStream != undefined ? 'text': 'number'"
						:class="changeMarkClass(field.Value.Value)"
						:id="field.Name"
						:ref="field.Name"
						:disabled="ConsultMode === objectDisplayingModeEnum.Read || field.Disabled"
						:readonly="(field.Value.AnswerStream != undefined) || field.ReadOnly"
						:required="field.Required"
						:state="field.State"
						:min="1"
						:max="10"
						number
						v-model="field.Value.Value"
						@keypress="onlyNumber($event, field.Value.Value)"
						@input="updateMark(field)" />
						<b-input-group-append>
							<!-- Efface la note -->
							<b-button :variant="'outline-' + Variant" size="sm" v-b-tooltip.noninteractive.hover :title="$t('Clear')" :disabled="ConsultMode === objectDisplayingModeEnum.Read" @click="clearMark(field)"><b-icon icon="eraser" /></b-button>
							<!-- Accès au formulaire Notes -->
							<Mark :ref="'mark' + field.Name" :MarkField="field" :Variant="Variant" />
							<b-button :variant="Variant" size="sm" v-b-tooltip.noninteractive.hover :title="$t('Edit')" :disabled="ConsultMode === objectDisplayingModeEnum.Read" @click="showMark(field.Name)"><b-icon icon="pencil-square" /></b-button>
						</b-input-group-append>
				</b-input-group>

				<SelectList v-if="field.Type === fieldTypeEnum.SelectList" :ConsultMode="ConsultMode" :Field.sync="field" />

				<MultiSelectList v-if="field.Type === fieldTypeEnum.MultiSelectList" :ConsultMode="ConsultMode" :Field.sync="field" />

				<b-form-checkbox-group v-if="field.Type === fieldTypeEnum.CheckList"
					v-model="field.Value"
					:id="field.Name"
					:name="field.Name"
					:options="field.ListItems"
					:disabled="(ConsultMode === objectDisplayingModeEnum.Read) || field.Disabled || field.ReadOnly"
					:required="field.Required"
					:state="field.State"
					value-field="Id"
					text-field="Label"
					class="" />

				<b-form-radio-group v-if="field.Type === fieldTypeEnum.OptionList"
					:id="field.Name"
					v-model="field.Value"
					:options="field.ListItems"
					:disabled="(ConsultMode === objectDisplayingModeEnum.Read) || field.Disabled || field.ReadOnly"
					:required="field.Required"
					:state="field.State"
					value-field="Id"
					text-field="Label"
					disabled-field="Disabled"
					plain
					class=""
					@change="updateValue(field)" />

				<b-form-textarea v-if="field.Type === fieldTypeEnum.TextArea"
					v-model="field.Value"
					:id="field.Name"
					:disabled="ConsultMode === objectDisplayingModeEnum.Read || field.Disabled"
					:readonly="field.ReadOnly"
					:required="field.Required"
					:state="field.State"
					no-auto-shrink
					no-resize
					:rows="field.RowsLength"
					class="" />

				<!-- Espace affichant l'erreur de validation du formulaire pour le champ -->
				<b-form-invalid-feedback v-if="fieldError(ParentFormName, field.Name)" :state="field.State">
					{{ fieldError(ParentFormName, field.Name) }}
				</b-form-invalid-feedback>
			</b-form-group>

		</div>
	</div>
</template>

<script lang="ts">
	import { mixins } from 'vue-class-component';
	import { Component, Prop, PropSync, Vue } from "vue-property-decorator";
	import { Getter } from "vuex-class";
	import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
	import FieldTypeEnum from "@/enums/fields/FieldTypeEnum";
	import FormField from "@/types/fields/FormField";
	import SelectList from "./Fields/SelectListField.vue";
	import MultiSelectList from "./Fields/MultiSelectListField.vue";
	import ObjectDisplayingModeEnum from "@/enums/ObjectDisplayingModeEnum";
	import IFormElement from "@/interfaces/IFormElement";
	import NumberField from "@/types/fields/NumberField";
	import MarkField from "@/types/fields/MarkField";
	import Mark from "./Fields/Mark.vue";
	import { MarkUtils } from '@/components/commons/Utils';
	import Dictionary from '@/types/Dictionary';
	import axios from 'axios';
	import { API_URL } from '@/App.settings';
	import DatePicker from './DatePicker.vue';
	import locales from "./locales/Fields.json";

	/** Composant d'affichage de champ en fonction de type géré par FormDrafting.cs côté server et décrit dans les contrôleurs enfants d'ObjectDisplaying */
	@Component({
		components: {
			FontAwesomeIcon,
			SelectList,
			MultiSelectList,
			Mark,
			DatePicker
		},
		i18n: { messages: locales }
	})
	export default class Fields extends mixins(MarkUtils) {
		/** Pour pouvoir utiliser les enums dans la section template */
		private fieldTypeEnum = FieldTypeEnum;
		private objectDisplayingModeEnum = ObjectDisplayingModeEnum;

		/** Variant de couleur à appliquer sur le composant */
		@Prop({default: () => "secondary"})
		public Variant!: string;

		/** Nom du formulaire de fiche objet parent */
		@Prop()
		public ParentFormName!: string;

		/** Indique si les champs du formulaire sont éditables ou en mode consultation */
		@Prop()
		public ConsultMode!: ObjectDisplayingModeEnum;

		/** Liste des champs du formulaire */
		@PropSync('Fields')
		public fields!: FormField[];

		/** Erreur associée à un champ */
		@Getter('ObjectStore/GetFieldError')
		private fieldError!: (formName: string, fieldName: string) => string;

		/** 
		 * Obtient le NodeId du noeud chargé dans la fiche objet
		 * @todo Retirer ce getter et récuperer le nodeId via @InjectReactive !!!!
		 **/
		@Getter('ObjectStore/GetNodeId')
		private nodeId!: string;

		/** Met à jour la valuer d'un Field dans l'ObjectStore
		 * @param element Element à mettre à jour
		 */
		private updateValue(element: IFormElement) {
			console.debug("Fields - updateValue - element : ", element);
			this.$store.dispatch("ObjectStore/UpdateValue", { newElement: element });
		}

		//#region Gestion des notes
		private showMark(fieldName: string) {
			(this.$refs['mark' + fieldName] as any)[0].Show();
		}

        /** 
		 * Efface un champ de type Mark (note)
		 * @param element Element à mettre à jour
		 */
		 private updateMark(element: IFormElement) {
			(element as MarkField).Value.NotModulatedMark = (element as MarkField).Value.Value;
			(element as MarkField).Value.Modulation = 0;
			(element as MarkField).Value.AnswerStream = undefined;
			this.updateValue(element);
		}

		/** 
		 * Efface un champ de type Mark (note)
		 * @param element Element à mettre à jour
		 */
		private clearMark(element: IFormElement) {
			(element as MarkField).Value.Value = undefined;
			(element as MarkField).Value.NotModulatedMark = 0;
			(element as MarkField).Value.Modulation = 0;
			(element as MarkField).Value.AnswerStream = undefined;
			this.updateValue(element);
		}

		//end#region Gestion des notes

		private isNumber(evt, element: NumberField) : boolean {
			//console.debug("Fields - isNumber - element : ", evt, element);
			let negative = element.Negative;
			let separator = element.Separator;
			let value = element.Value.toString();
			//console.debug("Fields - isNumber - negative, separator, value : ", negative, separator, value);

			let charCode = (evt.which) ? evt.which : evt.keyCode;
			if (charCode === 45) {
				if (negative && value.length === 0) {
					return true;
				} else {
					evt.preventDefault();
				}
			} else if ((charCode === 46 || charCode === 44)) {
				
				if (separator && value.length > 0 && value.indexOf('.') === -1 && value.indexOf(',') === -1) {
					return true;
				} else {
					evt.preventDefault();
				}
			} else if ((charCode > 31 && (charCode < 48 || charCode > 57)) 
				&& charCode !== 44 && charCode !== 45 && charCode !== 46 ) {
				evt.preventDefault();
			} else {
				return true;
			}
		}

		/**
		 * Formatte le texte renseigné en remplaçant les caractères non numériques
		 * @param value texte à formatter
		 * @returns texte formatté sans caractère non numériques
		 */
		private onlyNumber(evt, value): boolean {
			
			// transformation value en chaine de caractères (value peut etre undefined si on efface sa valeur)
			let strVal = value ? value.toString() : "";

			// code caractère saisie (48 -> 57 : chiffre de 0 à 9)
			let charCode = (evt.which) ? evt.which : evt.keyCode;

			console.debug("Fields - onlyNumber - evt : %O", evt);
			console.debug("Fields - onlyNumber - charCode : %O - strVal : %O", charCode, strVal);

			// Si valeur vide -> insertion uniquement chiffre 1 à 9
			if(strVal.length === 0)
				if(charCode >= 49 && charCode <= 57)
					return true;
				else
					evt.preventDefault();
			else if (value == "1" && charCode == 48)
				return true;
			else
				evt.preventDefault();
		}

		//#region Test du doublon
		private checkDuplicate(invariant : string){
			//Arguments
            const args: Dictionary<any> = {};
            args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
			args["NodeId"] = this.nodeId;
			args["Invariant"] = invariant;

			// Ecran de chargement
            this.$store.dispatch("ShowLoadingMask", this.$t('SearchingDuplicates').toString() /*"Recherche de doublons"*/);

			axios.post(API_URL + "object/Meter/CheckDuplicate", args).then((response) => {
				if(response.data){
					this.$bvToast.toast(this.$t('NoDuplicatesFound').toString(), { // Auncun doublon détécté
                            title: this.$t('Information').toString(),
                            autoHideDelay: 2000,
                            variant: "success",
                            solid: true,
                            appendToast: true,
                            toaster: "b-toaster-top-center"
                    });
				}
				else{
					this.$bvToast.toast(this.$t('DuplicateInvariantFound').toString(), {
                            title:  this.$t('Warning').toString(),
                            autoHideDelay: 2000,
                            variant: "warning",
                            solid: true,
                            appendToast: true,
                            toaster: "b-toaster-top-center"
                    });
				}
			}).finally(() => {
				this.$store.dispatch("HideLoadingMask", {});
			})	
		}
		//#endregion Test du doublon
	}
</script>

<style scoped>
	/** Variable --Variant injectée via generateVariantStyle(Variant) dans ObjectDisplaying (composant commun à toute la fiche objet) */
    :deep(input[type=checkbox]:checked) {
        /** Change la couleur de la checkbox */
        accent-color: var(--Variant) !important;
    }

    :deep(input[type=radio]:checked) {
        /** Change la couleur du bouton radio */
        accent-color: var(--Variant) !important;
    }
</style>